import type { ListEligibleMembershipsResponse } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import type { ControllerFlowAPI, Experiments } from '@wix/yoshi-flow-editor';
import settingsParams from '../../../../components/BookingsForm/settingsParams';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  PaymentMethod,
  ReservedPaymentOptionIds,
} from '../../../../types/types';
import type { ServicePayment } from '../../../mappers/serviceMapper/utils/payment.mapper';
import {
  DepositOrFullAmountOptionIds,
  isDepositOrFullAmountFeatureEnabled,
  isOfferedAsOneTimeOnly,
  isOfferedAsPricingPlanOnly,
  isServiceHavePricingPlans,
} from '../utils';

export const getDefaultPaymentOptionId = ({
  settings,
  servicePayment,
  memberships,
  isPricingPlanInstalled,
  isDynamicPricingCustomOptions,
  hasEligiblePlans,
  experiments,
  paymentTypes,
  excludePricingPlanOptions = false,
}: {
  settings: ControllerFlowAPI['settings'];
  servicePayment: ServicePayment;
  isPricingPlanInstalled: boolean;
  memberships?: ListEligibleMembershipsResponse;
  isDynamicPricingCustomOptions?: boolean;
  hasEligiblePlans?: boolean;
  experiments?: Experiments;
  paymentTypes?: PaymentMethod[];
  excludePricingPlanOptions?: boolean;
}) => {
  const getDefaultSingleSessionPaymentOptionId = () =>
    isDepositOrFullAmountFeatureEnabled({
      experiments,
      fullUpfrontPaymentAllowed:
        servicePayment.paymentDetails.fullUpfrontPaymentAllowed,
      deposit: servicePayment.paymentDetails.minCharge,
      paymentTypes: paymentTypes ?? [],
    })
      ? DepositOrFullAmountOptionIds.Deposit
      : ReservedPaymentOptionIds.SingleSession;

  if (
    servicePayment.paymentDetails.subscriptionInfo &&
    experiments?.enabled(ExperimentsConsts.SubscriptionPricingUoU)
  ) {
    return ReservedPaymentOptionIds.Subscription;
  }

  if (isDynamicPricingCustomOptions) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  const planId = excludePricingPlanOptions
    ? undefined
    : memberships?.eligibleMemberships?.[0]?.id; // todo: change to Selected Plan when implemented

  if (planId) {
    return planId;
  }
  if (
    excludePricingPlanOptions ||
    isOfferedAsOneTimeOnly(servicePayment) ||
    !isPricingPlanInstalled
  ) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  if (isOfferedAsPricingPlanOnly(servicePayment)) {
    return ReservedPaymentOptionIds.BuyAPricingPlan;
  }

  const selected =
    [
      PaymentMethod.SINGLE,
      PaymentMethod.ONLINE,
      PaymentMethod.OFFLINE,
    ].includes(settings.get(settingsParams.defaultPaymentMethod)) ||
    !isServiceHavePricingPlans(servicePayment) ||
    !hasEligiblePlans
      ? getDefaultSingleSessionPaymentOptionId()
      : ReservedPaymentOptionIds.BuyAPricingPlan;
  return selected;
};
